import React, { useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import Dropdown, { DropdownTheme } from 'Douleutaras/components/Dropdown/Dropdown';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ACTIVE_JOB_STATUSES, fetchJobs, fetchJobsStats, selectJobMetaByStatus } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { getAbsoluteUrl } from 'Douleutaras/utilities/url-builder';
import { URLS } from 'Douleutaras/constants/urls';
import { NotificationsDropdown } from 'Douleutaras/layout/partials/NotificationsDropdown';
import DefaultHeaderProMenu from 'Douleutaras/layout/partials/DefaultHeaderProMenu/DefaultHeaderProMenu';
import { fetchConversationsOptions } from 'Douleutaras/redux/data/conversations/conversationsSlice';
import { useConversationsInfo } from 'Douleutaras/hooks/conversations';
import { PusherContext } from 'Douleutaras/app/PusherContext';
import { useMediaQuery } from 'react-responsive';
import ChatButton from 'Douleutaras/layout/partials/ChatButton/ChatButton';
import { PHONE, TABLET } from 'Douleutaras/constants/media-queries';
import { TFetchJobs } from 'Douleutaras/redux/data/jobs/types';
import { useSelector } from 'react-redux';
import { RootState } from 'Douleutaras/redux/store';
import { fetchCategoriesMenu } from 'Douleutaras/redux/data/v2/categoriesMenu/categoriesMenuSlice';
import UserAvatar, { AvatarSize } from 'Douleutaras/components/UserAvatar/UserAvatar';
import placeholderImg from 'Assets/layout/job/icon-extra-bid-avatar.svg';
import { UserType } from 'Douleutaras/types';

import AllCategoriesDropdown from './AllCategoriesDropdown/AllCategoriesDropdown';
import DefaultHeaderUserMenu from './DefaultHeaderUserMenu/DefaultHeaderUserMenu';
import DefaultHeaderLogo from './DefaultHeaderLogo/DefaultHeaderLogo';
import DefaultHeaderMobile from './DefaultHeaderMobile/DefaultHeaderMobile';
import JobsDropdown from './JobsDropdown/JobsDropdown';
import './DefaultHeader.scss';

interface DefaultHeaderProps {
  showProHeader?: boolean; // Display Pros header
}

const DefaultHeader = ({ showProHeader }: DefaultHeaderProps) => {
  const user = useAppSelector(selectCurrentUser);
  const isLoggedIn = user && Boolean(Object.keys(user).length);
  const pusherChannel = React.useContext(PusherContext);
  const matchedRoute = useMatch({
    path: getAbsoluteUrl('PRO_DASHBOARD.INDEX'),
    end: false,
    caseSensitive: true
  });
  const isPro = showProHeader || (user?.user_type === UserType.SELLER || Boolean(matchedRoute));
  const matchedConversationThread = useMatch({
    path: isPro ? getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.THREAD') : getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.THREAD'),
    end: false,
    caseSensitive: true
  });
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [userDropdown, setUserDropdown] = useState(false);
  const [jobsDropdown, setJobsDropdown] = useState(false);
  const conversationsInfo = useConversationsInfo();
  const isMobile = useMediaQuery(
    { maxWidth: PHONE.MAX_WIDTH }
  );

  const isMaxTablet = useMediaQuery(
    { maxWidth: TABLET.MAX_WIDTH }
  );

  const activeJobsParams: Partial<TFetchJobs> = {
    includes: ['category', 'category.business_unit'],
    status: ACTIVE_JOB_STATUSES,
    page: '1'
  };
  const activeJobs = useAppSelector(state => selectJobMetaByStatus(
    state,
    // @ts-ignore
    { status: ACTIVE_JOB_STATUSES, page: 1 }
  ));

  const matchedConversationId = matchedConversationThread?.params['conversationId'] as string | undefined;
  const handleMessagesCreated = resource => {
    // Don't update the stats when received message is from the current focused conversation
    if (matchedConversationId !== String(resource.data.attributes.conversation_id)) {
      // Update conversationOptions on each message created.
      dispatch(fetchConversationsOptions({ includes: [] }));
    }
  };

  // If the app does not have activeJobs.data,
  // this effect should trigger and fetch Jobs Stats.
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    if (!isPro && !activeJobs?.data) {
      dispatch(fetchJobsStats(activeJobsParams));
    }
  }, []);

  // Gets updated when matchedConversationId changes
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    dispatch(fetchConversationsOptions({ includes: [] }));
    pusherChannel?.bind('messages_created', handleMessagesCreated);

    return () => {
      pusherChannel?.unbind('messages_created', handleMessagesCreated);
    };
  }, [ matchedConversationId ]);

  const openCallback = dropDownStatus => {
    setUserDropdown(dropDownStatus);
  };
  const openJobsCallback = dropDownStatus => { setJobsDropdown(dropDownStatus); };

  // Callback function for empty JobsListDropdown state (empty - means no activeJobs list is loaded but only jobsStats)
  const jobsListClicked = e => {
    // Redirect user to "dashboard" if no activeJobs is available
    if (activeJobs.meta.statusCounts.active === 0) {
      e.stopPropagation();
      navigate(URLS.CLIENT_DASHBOARD.INDEX);

      return;
    }
    // Otherwise, fetch activeJobs and open dropdown
    dispatch(fetchJobs(activeJobsParams));
    setJobsDropdown(true);
  };

  const settingsUrl = isPro ? getAbsoluteUrl('PRO_DASHBOARD.SETTINGS.GENERAL') : getAbsoluteUrl('CLIENT_DASHBOARD.SETTINGS');
  const isChatRoute = useMatch({
    path: isPro ? getAbsoluteUrl('PRO_DASHBOARD.CONVERSATION.INDEX') : getAbsoluteUrl('CLIENT_DASHBOARD.CONVERSATION.INDEX'),
    end: false,
    caseSensitive: true
  });

  const messagesCount = isPro ? conversationsInfo.unseenSellerConversationsCount : conversationsInfo.unseenBuyerConversationsCount;
  const isSettingsUrl = Boolean(useMatch(settingsUrl));

  useEffect(() => {
    dispatch(fetchCategoriesMenu(i18n.language));
  }, []);

  const { data: categoriesMenu, loading: menuLoading } = useSelector((state: RootState) => state.categoriesMenu);

  return (
    <>
      <header styleName="default-header">
        <div className="custom-container-fluid">
          <div styleName="default-header-inner">
            <div styleName="default-header-left">
              <DefaultHeaderLogo isPro={isPro} />
              { isPro && <DefaultHeaderProMenu /> }
            </div>
            <div styleName="default-header-right">
            {!isPro && !menuLoading && !isMobile &&
                <div styleName="default-header-item">
                  <AllCategoriesDropdown categoriesMenu={categoriesMenu} />
                </div>
              }
              { isLoggedIn && conversationsInfo?.chatEnabled &&
                <div styleName="default-header-item">
                  <ChatButton isChatRoute={isChatRoute} onlyIcon={isPro} messagesCount={messagesCount} />
                </div>
              }
              { isLoggedIn && isPro &&
                <div styleName="default-header-item">
                  <NotificationsDropdown/>
                </div>
              }
              { isLoggedIn && !isPro && activeJobs?.meta && !isMobile &&
                <div styleName="default-header-item">
                  <JobsDropdown
                    isOpen={jobsDropdown}
                    onToggle={openJobsCallback}
                    onHeaderClick={e => jobsListClicked(e)}
                    activeJobs={activeJobs}
                  />
                </div>
              }
              {!isLoggedIn &&
                <div styleName="default-header-pro">
                  <div styleName="default-header-pro-item">
                    <a styleName="default-header-pro-item__link" href={URLS.AUTHENTICATION.REGISTER_PRO}>
                      <span styleName="default-header-pro-item__icon" className="dashicon dashicon-user-pro"/>
                      <span styleName="default-header-pro-item__title">
                        {t('Professional Registration')}
                      </span>
                    </a>
                  </div>
                </div>
              }
              <div styleName="default-header-user">
                <article styleName="user-dropdown">
                  { isMaxTablet
                    ? <DefaultHeaderMobile
                        userDropdown={userDropdown}
                        openCallback={openCallback}
                        setUserDropdown={setUserDropdown}
                        isPro={isPro}
                        activeJobsCount={activeJobs.meta?.statusCounts.active}
                        unreadMessagesCount={isPro
                          ? conversationsInfo.unseenSellerConversationsCount
                          : conversationsInfo.unseenBuyerConversationsCount
                        }
                        categoriesMenu={categoriesMenu}
                      />
                    : <Dropdown
                        callback={openCallback}
                        isOpen={userDropdown}
                        theme={DropdownTheme.REGULAR}
                        header={
                          <header styleName="user-dropdown__header">
                            {!isLoggedIn && (
                              <button type="button" styleName={classNames({
                                'user-avatar-wrapper': true,
                                active: userDropdown
                                })} className="js-user-dropdown-trigger ">
                                <span className="dashicon dashicon-user" styleName="user-placeholder__icon"/>
                                <span styleName="user-avatar__text">
                                {t('Log in')}
                              </span>
                              </button>
                            )}
                            {isLoggedIn && (
                              <button type="button" styleName={
                                classNames({
                                  'user-avatar-wrapper': true,
                                  'user-avatar-wrapper--small': true,
                                  active: userDropdown
                                })
                              }>
                                {isPro
                                ? <UserAvatar mugshot={user.mugshot ? user.mugshot : placeholderImg }
                                              username={user.username}
                                              size={AvatarSize.SMALL} />
                                : <div styleName="user-avatar">
                                    <span styleName="user-avatar__letter">
                                      {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                                    </span>
                                  </div>
                                }
                                <span styleName="user-avatar__text">
                                  {isPro ? `${user.first_name} ${user.last_name}` : t('My Account')}
                                </span>
                                {isPro && <span styleName="user-avatar__arrow"/>}
                              </button>
                            )}
                          </header>
                        }
                        footer={
                          <DefaultHeaderUserMenu
                            setUserDropdown={setUserDropdown}
                            isPro={isPro}
                            isSettingsUrl={isSettingsUrl}
                          />
                        }
                      />
                  }
                </article>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div styleName="default-header-spacer"/>
    </>
  );
};

export default DefaultHeader;
